// Aportes y Crédito — DATOS REALES por cooperativa (aportes sociales, créditos de
// consumo, cuotas, provisión, reclasificación y castigo de cartera). Todo pasa por el
// motor contable del backend: cada operación genera su comprobante y exige que el
// período contable de la fecha esté abierto. Antes usaba el motor local de JS y mockData.

import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Tab, Tabs, Typography } from '@mui/material';
import { Add as AddIcon, CreditScore as CreditScoreIcon, Savings as SavingsIcon, Warning as WarningIcon, AccountBalance as AccountBalanceIcon } from '@mui/icons-material';
import EncabezadoOperativo from '../components/EncabezadoOperativo';
import FormularioDialog from '../components/FormularioDialog';
import TablaDatos from '../components/TablaDatos';
import useCargar from '../hooks/useCargar';
import useCooperativaOperativa from '../hooks/useCooperativaOperativa';
import { aportesApi, contabilidadApi, creditosApi, organizacionApi, tercerosApi } from '../services/coopnetApi';
import { fecha, fmt, hoy, moneda } from '../utils/formato';

const grid = (min) => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: 2 });
const COLOR_CREDITO = { Vigente: 'success', Pagado: 'default', Mora: 'error' };
const COLOR_CUOTA = { Pendiente: 'default', Pagada: 'success', Vencida: 'error' };

function CuotasDialog({ cooperativaId, credito, onClose, onPagar }) {
  const { datos, cargando, error } = useCargar(() => (credito ? creditosApi.cuotas(cooperativaId, credito.id) : null), [credito?.id], []);
  return (
    <Dialog open={Boolean(credito)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Cuotas del crédito</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error">{error}</Alert>}
        {cargando ? (
          <CircularProgress size={24} />
        ) : (
          <TablaDatos
            filas={datos}
            vacio="Este crédito no tiene cuotas."
            columnas={[
              { titulo: 'N.º', render: (c) => c.numero },
              { titulo: 'Vence', render: (c) => fecha(c.fechaVencimiento) },
              { titulo: 'Capital', align: 'right', render: (c) => moneda(c.capital) },
              { titulo: 'Interés', align: 'right', render: (c) => moneda(c.interes) },
              { titulo: 'Valor cuota', align: 'right', render: (c) => moneda(c.valorCuota) },
              { titulo: 'Saldo', align: 'right', render: (c) => moneda(c.saldo) },
              { titulo: 'Estado', render: (c) => <Chip size="small" color={COLOR_CUOTA[c.estado] || 'default'} label={c.estado} /> },
              { titulo: 'Pago', render: (c) => (c.fechaPago ? fecha(c.fechaPago) : '—') },
              {
                titulo: '',
                render: (c) => (c.estado !== 'Pagada' ? <Button size="small" onClick={() => onPagar(c)}>Pagar</Button> : null),
              },
            ]}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

const AportesCredito = () => {
  const op = useCooperativaOperativa();
  const id = op.cooperativaId;
  const [tab, setTab] = useState(0);
  const [dlg, setDlg] = useState(null); // { tipo, credito?, cuota? }

  const { datos, cargando, error, recargar } = useCargar(async () => {
    if (!id) return null;
    const [aportes, creditos, afiliados, terceros, refs, wallet] = await Promise.all([
      aportesApi.listar(id),
      creditosApi.listar(id),
      organizacionApi.listarAfiliados(id),
      tercerosApi.listar(id, 'Afiliado'),
      contabilidadApi.referencias(id),
      contabilidadApi.wallet(id).catch(() => null),
    ]);
    return { aportes, creditos, afiliados, terceros, refs, wallet };
  }, [id]);

  const afiliados = useMemo(() => datos?.afiliados || [], [datos]);
  const activos = afiliados.filter((a) => a.estado === 'activo');
  const nombreDe = useMemo(() => new Map(afiliados.map((a) => [a.id, `${a.nombre} ${a.apellido}`])), [afiliados]);
  const lineas = datos?.refs?.lineasCredito || [];
  const califs = datos?.refs?.calificaciones || [];
  const pctDe = (cal) => califs.find((c) => c.id === cal)?.provisionPct ?? 0;
  const terceroDe = (afiliadoId) => (datos?.terceros || []).find((t) => t.afiliadoId === afiliadoId)?.id || `afiliado-${afiliadoId}`;
  const opcionesAfiliado = activos.map((a) => ({ valor: a.id, etiqueta: `${a.nombre} ${a.apellido} — ${a.documento}` }));

  const creditos = datos?.creditos || [];
  const vigentes = creditos.filter((c) => c.estado === 'Vigente').length;
  const enMora = creditos.filter((c) => c.estado === 'Mora').length;
  const w = datos?.wallet;

  const camposAporte = [
    { campo: 'afiliadoId', label: 'Afiliado', tipo: 'select', requerido: true, opciones: opcionesAfiliado },
    { campo: 'tipo', label: 'Tipo de aporte', tipo: 'select', requerido: true, opciones: [{ valor: 'Ordinario', etiqueta: 'Ordinario' }, { valor: 'Extraordinario', etiqueta: 'Extraordinario' }] },
    { campo: 'monto', label: 'Monto', tipo: 'numero', requerido: true, positivo: true },
    { campo: 'fecha', label: 'Fecha', tipo: 'fecha', requerido: true },
    { campo: 'medioPagoCaja', label: 'Pagado en efectivo (caja). Si no, por bancos', tipo: 'switch' },
  ];
  const cuerpoAporte = (v) => ({ afiliadoId: v.afiliadoId, terceroId: terceroDe(v.afiliadoId), tipo: v.tipo, monto: v.monto, fecha: v.fecha, medioPagoCaja: v.medioPagoCaja });

  const c = dlg?.credito;
  const stats = [
    { titulo: 'Aportes sociales', valor: w ? moneda(w.aportesSocialesTotales) : '—', icono: SavingsIcon, color: '#2E7D32' },
    { titulo: 'Cartera de créditos', valor: w ? moneda(w.carteraCreditosBruta) : '—', icono: CreditScoreIcon, color: '#0D47A1' },
    { titulo: 'Provisión de cartera', valor: w ? moneda(w.provisionCartera) : '—', icono: AccountBalanceIcon, color: '#E65100' },
    { titulo: 'Créditos vigentes / en mora', valor: `${fmt(vigentes)} / ${fmt(enMora)}`, icono: WarningIcon, color: '#6A1B9A' },
  ];

  return (
    <EncabezadoOperativo
      titulo="Aportes y Crédito"
      subtitulo="Aportes sociales y crédito de consumo colaborativo"
      operativa={op}
      acciones={
        <>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setDlg({ tipo: 'aporte' })}>Registrar aporte</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDlg({ tipo: 'desembolso' })}>Desembolsar crédito</Button>
        </>
      }
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {cargando && !datos ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          <Box sx={{ ...grid(230), mb: 3 }}>
            {stats.map((s) => (
              <Card key={s.titulo}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${s.color}1A`, color: s.color, display: 'flex' }}><s.icono /></Box>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ color: s.color }}>{s.valor}</Typography>
                    <Typography variant="body2">{s.titulo}</Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tab label={`Aportes (${datos?.aportes?.length ?? 0})`} />
            <Tab label={`Créditos (${creditos.length})`} />
          </Tabs>

          {tab === 0 && (
            <>
              <Box sx={{ mb: 2 }}>
                <Button size="small" color="warning" onClick={() => setDlg({ tipo: 'retiro' })}>Retiro de aportes</Button>
              </Box>
              <TablaDatos
                filas={datos?.aportes || []}
                vacio="Aún no hay aportes registrados."
                columnas={[
                  { titulo: 'Fecha', render: (a) => fecha(a.fecha) },
                  { titulo: 'Afiliado', render: (a) => a.afiliado },
                  { titulo: 'Tipo', render: (a) => a.tipo },
                  { titulo: 'Monto', align: 'right', render: (a) => moneda(a.monto) },
                ]}
              />
            </>
          )}

          {tab === 1 && (
            <TablaDatos
              filas={creditos}
              vacio="Aún no hay créditos desembolsados."
              columnas={[
                { titulo: 'Afiliado', render: (k) => nombreDe.get(k.afiliadoId) || '—' },
                { titulo: 'Línea', render: (k) => lineas.find((l) => l.id === k.lineaCreditoId)?.nombre || k.lineaCreditoId },
                { titulo: 'Desembolso', render: (k) => fecha(k.fechaDesembolso) },
                { titulo: 'Aprobado', align: 'right', render: (k) => moneda(k.montoAprobado) },
                { titulo: 'Tasa / plazo', render: (k) => `${k.tasaInteresAnual}% · ${k.plazoMeses} m` },
                { titulo: 'Saldo capital', align: 'right', render: (k) => moneda(k.saldoCapital) },
                { titulo: 'Calif.', render: (k) => k.calificacionRiesgo },
                { titulo: 'Estado', render: (k) => <Chip size="small" color={COLOR_CREDITO[k.estado] || 'default'} label={k.estado} /> },
                {
                  titulo: 'Acciones',
                  render: (k) => (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Button size="small" onClick={() => setDlg({ tipo: 'cuotas', credito: k })}>Cuotas</Button>
                      {k.estado !== 'Pagado' && (
                        <>
                          <Button size="small" onClick={() => setDlg({ tipo: 'provision', credito: k })}>Provisionar</Button>
                          <Button size="small" onClick={() => setDlg({ tipo: 'reclasificar', credito: k })}>Reclasificar</Button>
                          <Button size="small" color="error" onClick={() => setDlg({ tipo: 'castigo', credito: k })}>Castigar</Button>
                        </>
                      )}
                    </Box>
                  ),
                },
              ]}
            />
          )}
        </>
      )}

      {/* Aporte / retiro */}
      <FormularioDialog
        abierto={dlg?.tipo === 'aporte' || dlg?.tipo === 'retiro'}
        titulo={dlg?.tipo === 'retiro' ? 'Retiro de aportes sociales' : 'Registrar aporte social'}
        campos={camposAporte}
        inicial={{ fecha: hoy(), tipo: 'Ordinario', medioPagoCaja: false }}
        textoBoton={dlg?.tipo === 'retiro' ? 'Registrar retiro' : 'Registrar aporte'}
        onGuardar={async (v) => {
          await (dlg.tipo === 'retiro' ? aportesApi.retirar : aportesApi.emitir)(id, cuerpoAporte(v));
          recargar();
        }}
        onClose={() => setDlg(null)}
      />

      {/* Desembolso */}
      <FormularioDialog
        abierto={dlg?.tipo === 'desembolso'}
        titulo="Desembolsar crédito"
        descripcion="Genera el comprobante y la tabla de amortización."
        campos={[
          { campo: 'afiliadoId', label: 'Afiliado', tipo: 'select', requerido: true, opciones: opcionesAfiliado },
          { campo: 'lineaCreditoId', label: 'Línea de crédito', tipo: 'select', requerido: true, opciones: lineas.map((l) => ({ valor: l.id, etiqueta: `${l.nombre} (hasta ${moneda(l.montoMaximo)})` })) },
          { campo: 'monto', label: 'Monto', tipo: 'numero', requerido: true, positivo: true },
          { campo: 'tasaAnual', label: 'Tasa de interés anual (%)', tipo: 'numero', requerido: true, positivo: true },
          { campo: 'plazoMeses', label: 'Plazo (meses)', tipo: 'numero', requerido: true, positivo: true },
          { campo: 'calificacionInicial', label: 'Calificación inicial', tipo: 'select', requerido: true, opciones: califs.map((k) => ({ valor: k.id, etiqueta: `${k.id} — ${k.nombre}` })) },
          { campo: 'fecha', label: 'Fecha de desembolso', tipo: 'fecha', requerido: true },
        ]}
        inicial={{ fecha: hoy(), lineaCreditoId: lineas[0]?.id, tasaAnual: lineas[0]?.tasaInteresAnualRef, plazoMeses: 12, calificacionInicial: 'A' }}
        textoBoton="Desembolsar"
        onGuardar={async (v) => {
          await creditosApi.desembolsar(id, {
            afiliadoId: v.afiliadoId, terceroId: terceroDe(v.afiliadoId), lineaCreditoId: Number(v.lineaCreditoId),
            monto: v.monto, tasaAnual: v.tasaAnual, plazoMeses: v.plazoMeses, fecha: v.fecha, calificacionInicial: v.calificacionInicial,
          });
          recargar();
        }}
        onClose={() => setDlg(null)}
      />

      <CuotasDialog cooperativaId={id} credito={dlg?.tipo === 'cuotas' ? dlg.credito : null} onClose={() => setDlg(null)} onPagar={(cuota) => setDlg({ tipo: 'pagarCuota', credito: dlg.credito, cuota })} />

      <FormularioDialog
        abierto={dlg?.tipo === 'pagarCuota'}
        titulo={`Pagar cuota ${dlg?.cuota?.numero ?? ''}`}
        descripcion={dlg?.cuota ? `Valor de la cuota: ${moneda(dlg.cuota.valorCuota)}` : ''}
        campos={[
          { campo: 'fecha', label: 'Fecha de pago', tipo: 'fecha', requerido: true },
          { campo: 'medioPagoCaja', label: 'Pagado en efectivo (caja). Si no, por bancos', tipo: 'switch' },
        ]}
        inicial={{ fecha: hoy(), medioPagoCaja: false }}
        textoBoton="Registrar pago"
        onGuardar={async (v) => {
          await creditosApi.pagarCuota(id, c.id, { numeroCuota: dlg.cuota.numero, fecha: v.fecha, medioPagoCaja: v.medioPagoCaja });
          recargar();
        }}
        onClose={() => setDlg(null)}
      />

      <FormularioDialog
        abierto={dlg?.tipo === 'provision'}
        titulo="Provisionar cartera"
        descripcion={c ? `Crédito con calificación ${c.calificacionRiesgo}. Saldo de capital: ${moneda(c.saldoCapital)}.` : ''}
        campos={[
          { campo: 'provisionPct', label: 'Provisión (%)', tipo: 'numero', requerido: true, positivo: true, ayuda: 'Por defecto, el % de la calificación actual' },
          { campo: 'fecha', label: 'Fecha', tipo: 'fecha', requerido: true },
        ]}
        inicial={{ fecha: hoy(), provisionPct: c ? pctDe(c.calificacionRiesgo) : 0 }}
        textoBoton="Provisionar"
        onGuardar={async (v) => { await creditosApi.provisionar(id, c.id, { provisionPct: v.provisionPct, fecha: v.fecha }); recargar(); }}
        onClose={() => setDlg(null)}
      />

      <FormularioDialog
        abierto={dlg?.tipo === 'reclasificar'}
        titulo="Reclasificar crédito"
        descripcion={c ? `Calificación actual: ${c.calificacionRiesgo}.` : ''}
        campos={[
          { campo: 'calificacionNueva', label: 'Nueva calificación', tipo: 'select', requerido: true, opciones: califs.filter((k) => k.id !== c?.calificacionRiesgo).map((k) => ({ valor: k.id, etiqueta: `${k.id} — ${k.nombre} (${k.provisionPct}%)` })) },
          { campo: 'provisionAnteriorSaldo', label: 'Saldo de provisión actual', tipo: 'numero', requerido: true, ayuda: 'Por defecto, saldo de capital × % de la calificación actual' },
          { campo: 'fecha', label: 'Fecha', tipo: 'fecha', requerido: true },
        ]}
        inicial={{ fecha: hoy(), provisionAnteriorSaldo: c ? Math.round((c.saldoCapital * pctDe(c.calificacionRiesgo)) / 100) : 0 }}
        textoBoton="Reclasificar"
        onGuardar={async (v) => { await creditosApi.reclasificar(id, c.id, { calificacionNueva: v.calificacionNueva, provisionAnteriorSaldo: v.provisionAnteriorSaldo, fecha: v.fecha }); recargar(); }}
        onClose={() => setDlg(null)}
      />

      <FormularioDialog
        abierto={dlg?.tipo === 'castigo'}
        titulo="Castigar cartera"
        descripcion="El saldo pasa a cuentas de orden. Esta operación genera comprobante y no se puede deshacer desde aquí."
        campos={[{ campo: 'fecha', label: 'Fecha del castigo', tipo: 'fecha', requerido: true }]}
        inicial={{ fecha: hoy() }}
        textoBoton="Castigar"
        onGuardar={async (v) => { await creditosApi.castigar(id, c.id, { fecha: v.fecha }); recargar(); }}
        onClose={() => setDlg(null)}
      />
    </EncabezadoOperativo>
  );
};

export default AportesCredito;
